import React from "react";
import { useTranslation } from "react-i18next";
import type { NotificationChannel, NotificationChannelMasters } from "../Types/NotificationTypes";
import { CHANNEL_TABLE_LABEL_KEY } from "../Utils/NotificationConstants";
import "../Styles/NotificationToggleCell.scss";
import "../Styles/ChannelMastersCard.scss";

interface ChannelMastersCardProps {
  channelMasters: NotificationChannelMasters;
  hasNoConfiguredNotifications: boolean;
  savingMaster: boolean;
  onMasterToggleChange: (_channel: NotificationChannel, _checked: boolean) => void;
}

const ChannelMastersCard: React.FC<ChannelMastersCardProps> = ({
  channelMasters,
  hasNoConfiguredNotifications,
  savingMaster,
  onMasterToggleChange,
}) => {
  const { t } = useTranslation();

  return (
    <div className="channel-masters-card">
      <div className="channel-masters-info">
        <span className="channel-masters-title">
          {t("notificationPreferences.channelMasters.title", {
            defaultValue: "Enable Notification",
          })}
        </span>
        <span className="channel-masters-subtitle">
          {t("notificationPreferences.channelMasters.description", {
            defaultValue:
              "Email, Push, and In-App toggle independently, turning one off disables it for every category below, including those currently enabled, without affecting the other two channels.",
          })}
        </span>
      </div>
      <div
        className={`channel-masters-toggles${
          hasNoConfiguredNotifications ? " channel-masters-toggles--disabled" : ""
        }`}
      >
        {(["email", "push", "in_app"] as const).map(channel => (
          <div className="channel-master-toggle" key={channel}>
            <span className="channel-master-toggle__label">
              {t(`notificationPreferences.table.${CHANNEL_TABLE_LABEL_KEY[channel]}`, {
                defaultValue:
                  channel === "email" ? "Email" : channel === "push" ? "Push" : "In-App",
              })}
            </span>
            <label
              className={`notif-toggle${
                hasNoConfiguredNotifications ? " notif-toggle--disabled" : ""
              }`}
              aria-label={t("notificationPreferences.channelMasters.toggleAriaLabel", {
                channel,
                defaultValue: `Toggle ${channel} notifications for every category`,
              })}
            >
              <input
                type="checkbox"
                checked={channelMasters[channel]}
                disabled={hasNoConfiguredNotifications || savingMaster}
                onChange={e => onMasterToggleChange(channel, e.target.checked)}
              />
              <span className="notif-toggle-slider" />
            </label>
          </div>
        ))}
        {hasNoConfiguredNotifications ? (
          <div className="channel-masters-disabled-tooltip" role="tooltip">
            {t("notificationPreferences.channelMasters.disabledTooltip", {
              defaultValue: "Set up at least one notification below before enabling a channel.",
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ChannelMastersCard;
